import React from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { FileBoxIcon } from 'lucide-react'

import { Image } from '@repo/ui'
import { Preview } from 'components'

interface PackagePreviewProps {
  /** Product-level title (e.g. "30mm Sticker Tag") — NOT the variant tier or the package handle. */
  title?: string | null
  image?: string | null
  quantity?: number | string | null
  sku?: string | null
  onChange?: any
  onRemove?: any
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

/**
 * VERA-790 — the standard preview for a package's product: image + title, then labeled
 * `Quantity:` and `SKU:` lines. Modeled on ProductPreview (a thin `Preview` wrapper) but with the
 * package-specific fields. Callers pass already-resolved values (the package handle name and the
 * variant tier are deliberately NOT shown as the title). Used in both the Add-packages and
 * Add-tags flows so the assigned package reads the same everywhere.
 */
// Memoized: the encode modals re-render on every tag scan (the counter updates). Without memo the
// preview's <Image> would re-render each scan and could replay its load skeleton; stable props skip
// the re-render entirely.
export const PackagePreview = React.memo((props: PackagePreviewProps) => {
  const { title, image, quantity, sku, onChange = false, onRemove = false, disabled = false, size = 'large' } = props
  const theme = useTheme()
  const mediaPx = size === 'small' ? 28 : 48

  return (
    <Preview size={size} item={title ?? ''} onChange={onChange} onRemove={onRemove} disabled={disabled}>
      {image ? (
        <Image
          src={image}
          alt={title ?? ''}
          fit="cover"
          sx={(theme) => ({
            width: mediaPx,
            height: mediaPx,
            borderRadius: '6px',
            border: `1px solid ${theme.palette.grey[200]}`,
            flexShrink: 0,
          })}
        />
      ) : (
        <Box
          sx={{
            width: mediaPx,
            height: mediaPx,
            borderRadius: '6px',
            bgcolor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FileBoxIcon strokeWidth={1} size={20} color={theme.palette.grey[400]} />
        </Box>
      )}
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {title}
        </Typography>
        {quantity != null && quantity !== '' && (
          <Typography variant="body3" color="text.secondary" noWrap>
            Quantity: {quantity}
          </Typography>
        )}
        {sku && (
          <Typography variant="body3" color="text.secondary" noWrap>
            SKU: {sku}
          </Typography>
        )}
      </Stack>
    </Preview>
  )
})
PackagePreview.displayName = 'PackagePreview'
